import CanvasKitInit, { type CanvasKit, type EmbindEnumEntity, type Paragraph, type TypefaceFontProvider } from "canvaskit-wasm/full";
import canvasKitWasmUrl from "canvaskit-wasm/bin/full/canvaskit.wasm";
import { oklchToCss } from "@/document/model/color";
import type { TextNode } from "@/document/model/nodes";
import { normalizeFillStack, normalizeGradientPaint, sampleGradientPaint, type Paint } from "@/document/model/styles";
import { resolvedLineHeight, type RichTextModel, type TypeStyle } from "@/document/model/text";
import { fetchGoogleFontBytes, registerTextLayoutCacheInvalidator } from "@/lib/GoogleFontLoader";

export interface TextLayoutRun {
  text: string;
  style: TypeStyle;
  x: number;
  y: number;
  width: number;
  start: number;
  end: number;
  advances: number[];
  lineHeight: number;
  baseline: number;
  fill?: Paint;
}

export interface CachedTextLayout {
  key: string;
  width: number;
  height: number;
  baseline: number;
  runs: TextLayoutRun[];
}

let canvasKitPromise: Promise<CanvasKit | undefined> | undefined;
let fontProviderPromise: Promise<TypefaceFontProvider | undefined> | undefined;

const layoutCache = new Map<string, CachedTextLayout>();
const maxLayoutCacheEntries = 300;
let measureContext: CanvasRenderingContext2D | undefined;
let layoutCacheGeneration = 0;

export function invalidateTextLayoutCache(): void {
  layoutCache.clear();
  measureContext = undefined;
  layoutCacheGeneration += 1;
}

registerTextLayoutCacheInvalidator(invalidateTextLayoutCache);

export function ensureCanvasKit(): Promise<CanvasKit | undefined> {
  if (!canvasKitPromise) {
    canvasKitPromise = CanvasKitInit({
      locateFile: file => (file.endsWith(".wasm") ? canvasKitWasmUrl : file),
    }).catch(() => undefined);
  }
  return canvasKitPromise;
}

export async function ensureCanvasKitFontProvider(): Promise<TypefaceFontProvider | undefined> {
  if (!fontProviderPromise) {
    fontProviderPromise = ensureCanvasKit().then(async canvasKit => {
      if (!canvasKit) {
        return undefined;
      }

      const provider = canvasKit.TypefaceFontProvider.Make();
      try {
        const fonts = await fetchGoogleFontBytes();
        for (const font of fonts) {
          provider.registerFont(font.bytes, font.family);
        }
      } catch {
        return provider;
      }
      return provider;
    });
  }
  return fontProviderPromise;
}

export function getCachedTextLayout(node: TextNode, zoomBucket: number): CachedTextLayout {
  const defaultFill = normalizeFillStack(node.style).at(-1);
  const key = layoutKey(node.text, defaultFill, node.bounds.width, zoomBucket);
  const cached = layoutCache.get(key);
  if (cached) {
    return cached;
  }

  const layout = shapeTextFallback(node, key);
  layoutCache.set(key, layout);
  if (layoutCache.size > maxLayoutCacheEntries) {
    const firstKey = layoutCache.keys().next().value;
    if (firstKey) {
      layoutCache.delete(firstKey);
    }
  }
  return layout;
}

async function shapeWithCanvasKit(node: TextNode): Promise<Paragraph | undefined> {
  const [canvasKit, provider] = await Promise.all([ensureCanvasKit(), ensureCanvasKitFontProvider()]);
  if (!canvasKit || !provider) {
    return undefined;
  }

  const style = node.text.style;
  const paragraphStyle = new canvasKit.ParagraphStyle({
    textAlign: textAlign(canvasKit, style.horizontalAlign),
    textStyle: new canvasKit.TextStyle({
      color: canvasKit.WHITE,
      fontFamilies: [style.fontFamily],
      fontSize: style.fontSize,
      heightMultiplier: resolvedLineHeight(style) / style.fontSize,
      letterSpacing: style.letterSpacing,
      fontStyle: {
        weight: fontWeight(canvasKit, style.fontWeight),
        width: canvasKit.FontWidth.Normal,
        slant: canvasKit.FontSlant.Upright,
      },
    }),
  });
  const builder = canvasKit.ParagraphBuilder.MakeFromFontProvider(paragraphStyle, provider);
  builder.addText(node.text.characters);
  const paragraph = builder.build();
  paragraph.layout(node.text.textLayout === "AUTO_WIDTH" ? 100000 : Math.max(1, node.bounds.width));
  builder.delete();
  return paragraph;
}

function shapeTextFallback(node: TextNode, key: string): CachedTextLayout {
  const baseStyle = node.text.style;
  const maxWidth = node.text.textLayout === "AUTO_WIDTH" ? Number.POSITIVE_INFINITY : Math.max(1, node.bounds.width);
  const runs: TextLayoutRun[] = [];
  let sourceIndex = 0;

  for (const hardLine of node.text.characters.split("\n")) {
    const segments = styledSegmentsForText(node.text, sourceIndex, hardLine.length, normalizeFillStack(node.style).at(-1));
    let lineSegments: StyledTextSegment[] = [];
    let lineStart = sourceIndex;
    let lineWidth = 0;

    for (const segment of segments) {
      for (const word of splitSegmentWords(segment)) {
        const wordWidth = measureTextWidth(word.text, word.style);
        if (lineSegments.length > 0 && lineWidth + wordWidth > maxWidth) {
          pushLineRuns(runs, lineSegments, lineStart, lineWidth, maxWidth);
          lineSegments = [];
          lineStart = word.start;
          lineWidth = 0;
          if (/^\s+$/.test(word.text)) {
            continue;
          }
        }

        lineSegments.push(word);
        lineWidth += wordWidth;
      }
    }

    pushLineRuns(runs, lineSegments, lineStart, lineWidth, maxWidth);
    sourceIndex += hardLine.length + 1;
  }
  const width = node.text.textLayout === "AUTO_WIDTH"
    ? Math.max(1, ...runs.map(run => run.x + run.width))
    : node.bounds.width;
  const height = Math.max(resolvedLineHeight(baseStyle), maxLineBottom(runs));
  return {
    key,
    width,
    height,
    baseline: baseStyle.fontSize * 0.82,
    runs,
  };
}

export function textIndexAtPoint(node: TextNode, point: { x: number; y: number }, zoomBucket: number): number {
  const layout = getCachedTextLayout(node, zoomBucket);
  const localX = point.x - node.bounds.x;
  const localY = point.y - node.bounds.y - verticalOffset(node.bounds.height, layout.height, node.text.style.verticalAlign);
  const lineRuns = runsForY(layout.runs, localY);
  const run = lineRuns.find(candidate => localX >= candidate.x && localX <= candidate.x + candidate.width) ?? lineRuns.at(-1) ?? layout.runs.at(-1);
  if (!run) {
    return 0;
  }

  const relativeX = Math.max(0, localX - run.x);
  let cursor = 0;
  for (let index = 0; index < run.text.length; index += 1) {
    const width = run.advances[index] ?? measureTextWidth(run.text[index]!, run.style);
    if (relativeX < cursor + width / 2) {
      return run.start + index;
    }
    cursor += width;
  }
  return run.end;
}

export function caretRectForTextIndex(node: TextNode, index: number, zoomBucket: number): { x: number; y: number; width: number; height: number } {
  const layout = getCachedTextLayout(node, zoomBucket);
  const safeIndex = Math.max(0, Math.min(index, node.text.characters.length));
  const run = layout.runs.find(candidate => safeIndex >= candidate.start && safeIndex <= candidate.end) ?? layout.runs.at(-1);
  if (!run) {
    return { x: node.bounds.x, y: node.bounds.y, width: 1, height: resolvedLineHeight(node.text.style) };
  }

  const preceding = run.text.slice(0, Math.max(0, safeIndex - run.start));
  const advance = advanceForPrefix(run, preceding.length);
  return {
    x: node.bounds.x + run.x + advance,
    y: node.bounds.y + verticalOffset(node.bounds.height, layout.height, node.text.style.verticalAlign) + run.y,
    width: 1,
    height: run.lineHeight,
  };
}

export function selectionRectsForTextRange(node: TextNode, start: number, end: number, zoomBucket: number): Array<{ x: number; y: number; width: number; height: number }> {
  const layout = getCachedTextLayout(node, zoomBucket);
  const from = Math.max(0, Math.min(start, end));
  const to = Math.min(node.text.characters.length, Math.max(start, end));
  const offsetY = verticalOffset(node.bounds.height, layout.height, node.text.style.verticalAlign);
  return layout.runs.flatMap(run => {
    const runStart = Math.max(from, run.start);
    const runEnd = Math.min(to, run.end);
    if (runStart >= runEnd) {
      return [];
    }

    const beforeLength = runStart - run.start;
    const selectedLength = runEnd - runStart;
    const before = advanceForPrefix(run, beforeLength);
    const selected = advanceForRange(run, beforeLength, selectedLength);
    return [{
      x: node.bounds.x + run.x + before,
      y: node.bounds.y + offsetY + run.y,
      width: Math.max(1, selected),
      height: run.lineHeight,
    }];
  });
}

interface StyledTextSegment {
  text: string;
  style: TypeStyle;
  fill?: Paint;
  start: number;
  end: number;
  width: number;
  advances: number[];
}

function styledSegmentsForText(text: RichTextModel, absoluteStart: number, length: number, defaultFill: Paint | undefined): StyledTextSegment[] {
  if (length === 0) {
    const style = { ...text.style };
    return [{
      text: "",
      style,
      fill: style.fill ?? defaultFill,
      start: absoluteStart,
      end: absoluteStart,
      width: 0,
      advances: [],
    }];
  }

  const segments: StyledTextSegment[] = [];
  let currentStyleKey = "";
  let currentText = "";
  let currentStart = absoluteStart;
  let currentStyle: TypeStyle = text.style;
  let currentFill = text.style.fill ?? defaultFill;

  for (let offset = 0; offset < length; offset += 1) {
    const index = absoluteStart + offset;
    const style = resolvedStyleAt(text, index);
    const fill = style.fill ?? defaultFill;
    const key = JSON.stringify([style, fill]);
    if (currentText && key !== currentStyleKey) {
      segments.push(createMeasuredSegment(currentText, currentStyle, currentFill, currentStart));
      currentText = "";
      currentStart = index;
    }

    currentStyleKey = key;
    currentStyle = style;
    currentFill = fill;
    currentText += text.characters[index] ?? "";
  }

  if (currentText) {
    segments.push(createMeasuredSegment(currentText, currentStyle, currentFill, currentStart));
  }

  return segments;
}

function splitSegmentWords(segment: StyledTextSegment): StyledTextSegment[] {
  const parts = segment.text.match(/\S+|\s+/g) ?? [segment.text];
  let start = segment.start;
  return parts.map(part => {
    const measured = createMeasuredSegment(part, segment.style, segment.fill, start);
    start += part.length;
    return measured;
  });
}

function pushLineRuns(
  runs: TextLayoutRun[],
  segments: StyledTextSegment[],
  lineStart: number,
  lineWidth: number,
  maxWidth: number,
): void {
  const sourceSegments = segments.length > 0
    ? segments
    : [createMeasuredSegment("", { fontFamily: "Inter", fontWeight: 400, fontSize: 12, lineHeight: "auto", letterSpacing: 0, horizontalAlign: "LEFT", verticalAlign: "TOP" }, undefined, lineStart)];
  const effectiveWidth = sourceSegments.reduce((sum, segment) => sum + segment.width, 0);
  const style = sourceSegments[0]!.style;
  let cursorX = alignLineX(effectiveWidth || lineWidth, maxWidth, style.horizontalAlign);
  const lineHeight = Math.max(...sourceSegments.map(segment => resolvedLineHeight(segment.style)));
  const baseline = Math.max(...sourceSegments.map(segment => segment.style.fontSize * 0.82));
  const y = maxLineBottom(runs);

  for (const segment of sourceSegments) {
    runs.push({
      text: segment.text,
      style: segment.style,
      fill: segment.fill,
      x: cursorX,
      y,
      width: segment.width,
      start: segment.start,
      end: segment.end,
      advances: segment.advances,
      lineHeight,
      baseline,
    });
    cursorX += segment.width;
  }
}

function createMeasuredSegment(text: string, style: TypeStyle, fill: Paint | undefined, start: number): StyledTextSegment {
  const advances = measureTextAdvances(text, style);
  return {
    text,
    style,
    fill,
    start,
    end: start + text.length,
    width: advances.reduce((sum, advance) => sum + advance, 0),
    advances,
  };
}

function measureTextAdvances(text: string, style: TypeStyle): number[] {
  const characters = [...text];
  if (characters.length === 0) {
    return [];
  }

  const context = getMeasureContext();
  context.font = fontString(style);
  return measuredTextAdvances(text, style.letterSpacing, value => context.measureText(value).width);
}

export function measuredTextAdvances(text: string, letterSpacing: number, measure: (text: string) => number): number[] {
  const characters = [...text];
  if (characters.length === 0) {
    return [];
  }

  if (letterSpacing !== 0) {
    // Tracking is applied after every glyph, including the last one. Text is laid out as
    // separate word/whitespace runs, so trailing tracking must be included per character;
    // otherwise single-space runs (length 1) would receive no spacing at all, and run widths
    // would not match how drawTextWithLetterSpacing advances the cursor while drawing.
    return characters.map(character => measure(character) + letterSpacing);
  }

  let previousWidth = 0;
  return characters.map((_, index) => {
    const width = measure(characters.slice(0, index + 1).join(""));
    const advance = width - previousWidth;
    previousWidth = width;
    return advance;
  });
}

function resolvedStyleAt(text: RichTextModel, index: number): TypeStyle {
  const overrideId = text.characterStyleOverrides[index] ?? 0;
  return overrideId === 0
    ? text.style
    : { ...text.style, ...(text.styleOverrideTable[overrideId] ?? {}) };
}

function measureTextWidth(text: string, style: TypeStyle): number {
  if (!text) {
    return 0;
  }

  const context = getMeasureContext();
  context.font = fontString(style);
  return context.measureText(text).width + text.length * style.letterSpacing;
}

function fontString(style: TypeStyle): string {
  return `${style.fontWeight} ${style.fontSize}px "${style.fontFamily}", sans-serif`;
}

function advanceForPrefix(run: TextLayoutRun, length: number): number {
  return run.advances.slice(0, length).reduce((sum, advance) => sum + advance, 0);
}

function advanceForRange(run: TextLayoutRun, start: number, length: number): number {
  return run.advances.slice(start, start + length).reduce((sum, advance) => sum + advance, 0);
}

function maxLineBottom(runs: TextLayoutRun[]): number {
  if (runs.length === 0) {
    return 0;
  }

  return Math.max(...runs.map(run => run.y + run.lineHeight));
}

function runsForY(runs: TextLayoutRun[], y: number): TextLayoutRun[] {
  if (runs.length === 0) {
    return [];
  }

  const hit = runs.filter(run => y >= run.y && y < run.y + run.lineHeight);
  if (hit.length > 0) {
    return hit;
  }

  const firstLineY = Math.min(...runs.map(run => run.y));
  if (y < firstLineY) {
    return runs.filter(run => run.y === firstLineY);
  }

  const lastLineY = Math.max(...runs.map(run => run.y));
  return runs.filter(run => run.y === lastLineY);
}

function getMeasureContext(): CanvasRenderingContext2D {
  if (!measureContext) {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Unable to create text measurement context.");
    }
    measureContext = context;
  }
  return measureContext;
}

function layoutKey(text: RichTextModel, defaultFill: Paint | undefined, width: number, zoomBucket: number): string {
  return JSON.stringify([layoutCacheGeneration, text, defaultFill, width]);
}

export function paintToCanvasFillStyle(
  context: CanvasRenderingContext2D,
  paint: Paint | undefined,
  bounds: { x: number; y: number; width: number; height: number },
): string | CanvasGradient | undefined {
  if (!paint) {
    return undefined;
  }

  if (paint.type === "solid") {
    return oklchToCss(paint.color);
  }

  if (paint.type === "image") {
    return "#111827";
  }

  const normalized = normalizeGradientPaint(paint);
  const stops = sampleGradientPaint(normalized, 24);
  if (normalized.type === "radialGradient") {
    const cx = bounds.width * (normalized.center?.x ?? 0.5);
    const cy = bounds.height * (normalized.center?.y ?? 0.5);
    const radius = Math.max(bounds.width, bounds.height) * normalized.radius;
    const gradient = context.createRadialGradient(cx, cy, 0, cx, cy, Math.max(1, radius));
    for (const stop of stops) {
      gradient.addColorStop(stop.position, oklchToCss(stop.color));
    }
    return gradient;
  }

  const fallback = gradientVectorForBounds(bounds.width, bounds.height, normalized.angle);
  const start = normalized.start ?? fallback.start;
  const end = normalized.end ?? fallback.end;
  const gradient = context.createLinearGradient(start.x * bounds.width, start.y * bounds.height, end.x * bounds.width, end.y * bounds.height);
  for (const stop of stops) {
    gradient.addColorStop(stop.position, oklchToCss(stop.color));
  }
  return gradient;
}

function gradientVectorForBounds(width: number, height: number, angle: number): { start: { x: number; y: number }; end: { x: number; y: number } } {
  const radians = angle * Math.PI / 180;
  const dx = Math.cos(radians);
  const dy = Math.sin(radians);
  const radius = Math.abs(dx) * 0.5 + Math.abs(dy) * 0.5 * (height / Math.max(1, width));
  return {
    start: { x: 0.5 - dx * radius, y: 0.5 - dy * radius },
    end: { x: 0.5 + dx * radius, y: 0.5 + dy * radius },
  };
}

function alignLineX(lineWidth: number, maxWidth: number, align: TypeStyle["horizontalAlign"]): number {
  if (!Number.isFinite(maxWidth) || align === "LEFT" || align === "JUSTIFIED") {
    return 0;
  }
  if (align === "CENTER") {
    return Math.max(0, (maxWidth - lineWidth) / 2);
  }
  return Math.max(0, maxWidth - lineWidth);
}

function verticalOffset(boxHeight: number, textHeight: number, align: TypeStyle["verticalAlign"]): number {
  if (align === "CENTER") {
    return Math.max(0, (boxHeight - textHeight) / 2);
  }
  if (align === "BOTTOM") {
    return Math.max(0, boxHeight - textHeight);
  }
  return 0;
}

function textAlign(canvasKit: CanvasKit, align: TypeStyle["horizontalAlign"]): EmbindEnumEntity {
  switch (align) {
    case "CENTER":
      return canvasKit.TextAlign.Center;
    case "RIGHT":
      return canvasKit.TextAlign.Right;
    case "JUSTIFIED":
      return canvasKit.TextAlign.Justify;
    case "LEFT":
    default:
      return canvasKit.TextAlign.Left;
  }
}

function fontWeight(canvasKit: CanvasKit, weight: number): EmbindEnumEntity {
  if (weight >= 900) {
    return canvasKit.FontWeight.Black;
  }
  if (weight >= 800) {
    return canvasKit.FontWeight.ExtraBold;
  }
  if (weight >= 700) {
    return canvasKit.FontWeight.Bold;
  }
  if (weight >= 600) {
    return canvasKit.FontWeight.SemiBold;
  }
  if (weight >= 500) {
    return canvasKit.FontWeight.Medium;
  }
  if (weight <= 300) {
    return canvasKit.FontWeight.Light;
  }
  return canvasKit.FontWeight.Normal;
}
